// frontend/src/rma/RMAWizardContext.jsx
//
// Single mount point for the RMA New-Case wizard modal.
//
// Provides `useRMAWizard()` hook so any consumer (RMAOverviewLayout, the
// global "+ Add" menu in SidebarAndHeaderBar, MobileBottomNav's Create
// sheet) can call `openWizard()` to surface the wizard as a responsive
// Dialog (desktop) / SwipeableDrawer (mobile) overlay — instead of
// navigating away to /rma/new.
//
// The /rma/new URL still works as a deep-link fallback (see the route
// shim in RMANewCaseWizard.jsx): mounting that route opens the wizard
// modal then replaces the URL to /rma so back-button is sane.
//
// Per Samuel 2026-05-20.

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import RMANewCaseWizardModal from './RMANewCaseWizardModal'

const RMAWizardContext = createContext({
  open: false,
  openWizard: () => {},
  closeWizard: () => {},
})

/**
 * Hook for any component to open / close the RMA new-case wizard modal.
 *
 * Usage:
 *   const { openWizard } = useRMAWizard()
 *   <Button onClick={openWizard}>New Case</Button>
 */
export function useRMAWizard() {
  return useContext(RMAWizardContext)
}

/**
 * Provider — mount once at the app root (inside the Router so the wizard's
 * internal useNavigate still works after case creation).
 */
export function RMAWizardProvider({ children }) {
  const [open, setOpen] = useState(false)

  const openWizard = useCallback(() => setOpen(true), [])
  const closeWizard = useCallback(() => setOpen(false), [])

  // Memoize so consumers don't re-render when other parents do.
  const value = useMemo(
    () => ({ open, openWizard, closeWizard }),
    [open, openWizard, closeWizard],
  )

  return (
    <RMAWizardContext.Provider value={value}>
      {children}
      <RMANewCaseWizardModal open={open} onClose={closeWizard} />
    </RMAWizardContext.Provider>
  )
}
